<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { formatLeadCurrency, taskColor, taskDotClass } from '../helper';

const props = defineProps({
  lead: { type: Object, required: true },
  // En móvil (sin drag) se muestra un botón "Mover de etapa" explícito.
  movable: { type: Boolean, default: false },
});

const emit = defineEmits(['open', 'edit', 'move']);

const { t } = useI18n();

// Distingue un click real de un drag: tras arrastrar la tarjeta el navegador
// dispara un `click`; solo emitimos 'edit' si el puntero casi no se movió.
let pointerDown = { x: 0, y: 0 };

const onPointerDown = event => {
  pointerDown = { x: event.clientX, y: event.clientY };
};

const onClick = event => {
  const moved =
    Math.abs(event.clientX - pointerDown.x) +
    Math.abs(event.clientY - pointerDown.y);
  if (moved <= 6) emit('edit', props.lead);
};

const conversationLabel = computed(() =>
  props.lead.conversation ? `#${props.lead.conversation.display_id}` : ''
);

const formattedValue = computed(() => {
  const amount = Number(props.lead.value);
  if (!amount) return null;
  return formatLeadCurrency(amount, props.lead.currency);
});

const dotClass = computed(() => taskDotClass(props.lead.next_task_at));
const dotTitle = computed(() => {
  const color = taskColor(props.lead.next_task_at);
  if (color === 'ruby') return t('LEADS.TASK.OVERDUE');
  if (color === 'teal') return t('LEADS.TASK.TODAY');
  return '';
});
</script>

<template>
  <div
    class="flex flex-col gap-2 p-3 border rounded-lg shadow-sm cursor-pointer bg-n-solid-1 border-n-weak hover:border-n-slate-6"
    @pointerdown="onPointerDown"
    @click="onClick"
  >
    <div class="inline-flex items-center gap-1">
      <span class="text-sm font-medium text-n-slate-12 line-clamp-2">
        {{ lead.name }}
      </span>
      <span
        v-if="dotClass"
        class="size-2 rounded-full shrink-0"
        :class="dotClass"
        :title="dotTitle"
      />
    </div>
    <span v-if="formattedValue" class="text-xs font-semibold text-n-teal-11">
      {{ formattedValue }}
    </span>
    <div class="flex items-center justify-between gap-2 mt-0.5">
      <Avatar
        v-if="lead.assignee"
        :name="lead.assignee.name"
        :src="lead.assignee.thumbnail || ''"
        :size="20"
      />
      <span v-else class="text-xs text-n-slate-10">{{
        t('LEADS.UNASSIGNED')
      }}</span>
      <div class="flex items-center gap-2">
        <Button
          v-if="lead.conversation"
          link
          size="xs"
          icon="i-lucide-message-circle"
          :label="conversationLabel"
          @click.stop="emit('open', lead)"
        />
        <Button
          v-if="movable"
          faded
          color="slate"
          size="xs"
          icon="i-lucide-arrow-right-left"
          :label="t('LEADS.MOVE')"
          @click.stop="emit('move', lead)"
        />
      </div>
    </div>
  </div>
</template>
